import { useEffect, useState, useCallback } from "react";
import { Shield, Plug } from "lucide-react";
import { theme, card, inputStyle, btnPrimary, emptyFrame } from "../theme";
import { StatusTag, type StatusTone } from "../components/Drawing";
import { LoadingBlock } from "../components/Spinner";
import { api } from "../api/client";
import { ConnectorTrendPanel } from "../components/ConnectorTrendPanel";

interface IntuneStatus {
  connected:    boolean;
  tenantId:     string | null;
  clientId:     string | null;
  lastSync:     string | null;
  linkedRiskId: string | null;
  summary: {
    total:        number;
    compliant:    number;
    noncompliant: number;
    unknown:      number;
    other:        number;
  };
  /** Detected apps per device → the software inventory the vulnerability exposure pass reads (backend services/assetSoftware.ts). */
  software?: { lastSync: string | null; error: string | null; devices: number; apps: number };
}

interface IntuneDevice {
  id:              string;
  deviceName:      string;
  operatingSystem: string | null;
  osVersion:       string | null;
  complianceState: string;
  userDisplayName: string | null;
  manufacturer:    string | null;
  model:           string | null;
  lastSyncDateTime: string | null;
  snapshotAt:      string;
}

const STATE_COLORS: Record<string, { tone: StatusTone; label: string }> = {
  compliant:     { tone: "met",     label: "Compliant" },
  noncompliant:  { tone: "gap",     label: "Non-compliant" },
  unknown:       { tone: "none",    label: "Unknown" },
  inGracePeriod: { tone: "partial", label: "Grace Period" },
  notApplicable: { tone: "none",    label: "N/A" },
  conflict:      { tone: "gap",     label: "Conflict" },
  error:         { tone: "gap",     label: "Error" },
};

function StatePill({ state }: { state: string }) {
  const c = STATE_COLORS[state] ?? { tone: "none", label: state };
  return <StatusTag tone={c.tone}>{c.label}</StatusTag>;
}

function fmtDate(iso: string | null): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleString(undefined, { dateStyle: "short", timeStyle: "short" });
}

export function ConnectIntunePage() {
  const [status,    setStatus]    = useState<IntuneStatus | null>(null);
  const [devices,   setDevices]   = useState<IntuneDevice[]>([]);
  const [filter,    setFilter]    = useState<string>("all");
  const [loading,   setLoading]   = useState(true);
  const [syncing,   setSyncing]   = useState(false);
  const [saving,    setSaving]    = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [error,     setError]     = useState<string | null>(null);
  const [showForm,  setShowForm]  = useState(false);

  const [tenantId,     setTenantId]     = useState("");
  const [clientId,     setClientId]     = useState("");
  const [clientSecret, setClientSecret] = useState("");

  const loadStatus = useCallback(async () => {
    try {
      const data = await api.get<IntuneStatus>("/intune/status");
      setStatus(data);
      if (data.connected) {
        const { devices: devs } = await api.get<{ devices: IntuneDevice[] }>(`/intune/devices?filter=${filter}`);
        setDevices(devs);
      }
    } catch {
      setError("Failed to load Intune status.");
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => { loadStatus(); }, [loadStatus]);

  async function handleConnect(e: React.FormEvent) {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      await api.post("/intune/connect", { tenantId, clientId, clientSecret });
      setShowForm(false);
      setTenantId(""); setClientId(""); setClientSecret("");
      await loadStatus();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Connection failed");
    } finally {
      setSaving(false);
    }
  }

  async function handleSync() {
    setSyncing(true);
    setError(null);
    try {
      await api.post("/intune/sync", {});
      // Poll briefly then reload
      await new Promise((r) => setTimeout(r, 3000));
      await loadStatus();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Sync failed");
    } finally {
      setSyncing(false);
    }
  }

  async function handleDisconnect() {
    if (!confirm("Remove Intune integration and delete all synced device data?")) return;
    setDisconnecting(true);
    try {
      await api.del("/intune/connect");
      setDevices([]);
      await loadStatus();
    } catch {
      setError("Disconnect failed");
    } finally {
      setDisconnecting(false);
    }
  }

  if (loading) {
    return <LoadingBlock label="Loading Intune status…" />;
  }
  if (!status) return <div style={{ color: "var(--c-dangerText)", padding: "20px" }}>{error || "Failed to load status"}</div>;

  const s = status;

  return (
    <div>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 20 }}>
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 4 }}>
            <Shield size={22} color="var(--c-accentText)" />
            <h2 style={{ margin: 0, fontSize: 17, fontWeight: 700, color: theme.color.heading }}>
              Microsoft Intune
            </h2>
            {s.connected && (
              <span style={{ background: "var(--c-successBg)", color: "var(--c-successText)", fontSize: 11, fontWeight: 600, padding: "2px 9px", borderRadius: 3 }}>
                Connected
              </span>
            )}
          </div>
          <p style={{ margin: 0, fontSize: 13, color: theme.color.subtle }}>
            Pull managed device compliance states from Microsoft Intune via Graph API.
            Non-compliant devices automatically raise the linked risk entry in your Risk Register.
          </p>
        </div>
        {s.connected && (
          <div style={{ display: "flex", gap: 8, flexShrink: 0 }}>
            <button
              onClick={handleSync}
              disabled={syncing}
              style={{ ...btnPrimary, padding: "7px 14px", fontSize: 13, opacity: syncing ? 0.6 : 1, cursor: syncing ? "not-allowed" : "pointer" }}
            >
              {syncing ? "Syncing…" : "Sync Now"}
            </button>
            <button
              onClick={handleDisconnect}
              disabled={disconnecting}
              style={{ padding: "7px 14px", fontSize: 13, borderRadius: 3, border: "1px solid var(--c-border)", background: "var(--c-panel)", color: "var(--c-dangerText)", cursor: "pointer", opacity: disconnecting ? 0.6 : 1 }}
            >
              Disconnect
            </button>
          </div>
        )}
      </div>

      {error && (
        <div style={{ ...card, marginBottom: 16, padding: "10px 14px", background: "var(--c-dangerBg)", border: "1px solid var(--c-danger)", color: "var(--c-dangerText)", fontSize: 13 }}>
          {error}
        </div>
      )}

      {/* Not connected */}
      {!s.connected && !showForm && (
        <div style={{ ...emptyFrame, padding: "40px 28px" }}>
          <div style={{ marginBottom: 12, display: "flex", justifyContent: "center" }}><Plug size={36} color="var(--c-muted)" /></div>
          <div style={{ fontSize: 15, fontWeight: 600, color: theme.color.heading, marginBottom: 8 }}>
            Connect Azure Intune
          </div>
          <p style={{ fontSize: 13, color: theme.color.muted, maxWidth: 520, margin: "0 auto 20px", lineHeight: 1.6 }}>
            Have your IT admin create an app registration in your Azure tenant, grant it
            <strong> DeviceManagementManagedDevices.Read.All</strong> application permission, then provide
            the credentials below. Device compliance data syncs every 6 hours.
          </p>
          <p style={{ fontSize: 12, color: theme.color.muted, maxWidth: 520, margin: "-8px auto 20px", lineHeight: 1.6 }}>
            Optional: add <strong>DeviceManagementManagedDevices.PrivilegedOperations.All</strong> if you want
            remediation playbooks to ask a device to sync. Each such action runs only after a named person
            approves it.
          </p>
          <button onClick={() => setShowForm(true)} style={{ ...btnPrimary, padding: "9px 22px", fontSize: 13, cursor: "pointer" }}>
            Enter Credentials
          </button>
        </div>
      )}

      {/* Connect form */}
      {!s.connected && showForm && (
        <div style={{ ...card }}>
          <h3 style={{ margin: "0 0 16px", fontSize: 14, fontWeight: 600, color: theme.color.heading }}>
            App Registration Credentials
          </h3>
          <p style={{ fontSize: 12, color: theme.color.subtle, margin: "0 0 16px", lineHeight: 1.6 }}>
            In the Azure portal, go to <strong>App registrations → New registration</strong>, then under
            <strong> API permissions</strong> add <code>DeviceManagementManagedDevices.Read.All</code> (Application type)
            and grant admin consent (optionally also <code>DeviceManagementManagedDevices.PrivilegedOperations.All</code>,
            for remediation playbooks that ask a device to sync). Copy the values below from the app's Overview page.
          </p>
          <form onSubmit={handleConnect} style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            <label style={{ display: "flex", flexDirection: "column", gap: 5, fontSize: 13 }}>
              <span style={{ fontWeight: 500, color: theme.color.text }}>Directory (Tenant) ID</span>
              <input
                type="text"
                value={tenantId}
                onChange={(e) => setTenantId(e.target.value)}
                placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                required
                style={{ ...inputStyle, fontFamily: "monospace", fontSize: 13 }}
              />
            </label>
            <label style={{ display: "flex", flexDirection: "column", gap: 5, fontSize: 13 }}>
              <span style={{ fontWeight: 500, color: theme.color.text }}>Application (Client) ID</span>
              <input
                type="text"
                value={clientId}
                onChange={(e) => setClientId(e.target.value)}
                placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                required
                style={{ ...inputStyle, fontFamily: "monospace", fontSize: 13 }}
              />
            </label>
            <label style={{ display: "flex", flexDirection: "column", gap: 5, fontSize: 13 }}>
              <span style={{ fontWeight: 500, color: theme.color.text }}>Client Secret (Value)</span>
              <input
                type="password"
                value={clientSecret}
                onChange={(e) => setClientSecret(e.target.value)}
                placeholder="Secret value from Azure"
                required
                style={{ ...inputStyle, fontSize: 13 }}
              />
            </label>
            <div style={{ display: "flex", gap: 10, marginTop: 4 }}>
              <button type="submit" disabled={saving} style={{ ...btnPrimary, padding: "9px 20px", fontSize: 13, cursor: saving ? "not-allowed" : "pointer", opacity: saving ? 0.6 : 1 }}>
                {saving ? "Validating & Connecting…" : "Connect"}
              </button>
              <button type="button" onClick={() => setShowForm(false)} style={{ padding: "9px 16px", fontSize: 13, borderRadius: 3, border: "1px solid var(--c-border)", background: "var(--c-panel)", color: theme.color.muted, cursor: "pointer" }}>
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Connected — summary + device table */}
      {s.connected && (
        <>
          {/* Summary row */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12, marginBottom: 20 }}>
            {[
              { label: "Total Devices",   value: s.summary.total,        bg: theme.color.panel,           text: theme.color.heading },
              { label: "Compliant",        value: s.summary.compliant,    bg: "var(--c-successBg)",        text: "var(--c-successText)" },
              { label: "Non-compliant",    value: s.summary.noncompliant, bg: "var(--c-dangerBg)",         text: "var(--c-dangerText)" },
              { label: "Unknown",          value: s.summary.unknown,      bg: "var(--c-warningBg)",        text: "var(--c-warningText)" },
            ].map((stat) => (
              <div key={stat.label} style={{ ...card, background: stat.bg, padding: "14px 16px", textAlign: "center" }}>
                <div style={{ fontSize: 26, fontWeight: 700, color: stat.text }}>{stat.value}</div>
                <div style={{ fontSize: 12, color: stat.text, marginTop: 2, opacity: 0.8 }}>{stat.label}</div>
              </div>
            ))}
          </div>

          {/* Continuous monitoring: the daily readings over 90 days */}
          <ConnectorTrendPanel connector="intune" />

          {/* Meta info */}
          <div style={{ ...card, padding: "12px 16px", marginBottom: 20, display: "flex", gap: 24, flexWrap: "wrap", fontSize: 12, color: theme.color.muted }}>
            <span>Tenant: <strong style={{ color: theme.color.text, fontFamily: "monospace" }}>{s.tenantId}</strong></span>
            <span>Client: <strong style={{ color: theme.color.text, fontFamily: "monospace" }}>{s.clientId}</strong></span>
            <span>Last sync: <strong style={{ color: theme.color.text }}>{fmtDate(s.lastSync)}</strong></span>
            {s.linkedRiskId && <span>Risk Register: <strong style={{ color: "var(--c-accent)" }}>Auto-managed</strong></span>}
            {s.software && (
              <span title="The apps Intune detects on each device, stored on the device's asset so Threat Intelligence can compare installed versions with the NVD's affected ranges">
                Software inventory: {s.software.error
                  ? <strong style={{ color: "var(--c-warningText)" }}>{s.software.error}</strong>
                  : s.software.lastSync
                  ? <strong style={{ color: theme.color.text }}>{s.software.apps.toLocaleString()} apps on {s.software.devices.toLocaleString()} devices · {fmtDate(s.software.lastSync)}</strong>
                  : <strong style={{ color: theme.color.text }}>read with the next sync</strong>}
              </span>
            )}
          </div>

          {/* Filter tabs */}
          <div style={{ display: "flex", gap: 6, marginBottom: 14 }}>
            {["all", "noncompliant", "compliant", "unknown"].map((f) => {
              const active = filter === f;
              return (
                <button
                  key={f}
                  onClick={() => setFilter(f)}
                  style={{
                    padding: "5px 13px",
                    borderRadius: 3,
                    fontSize: 12,
                    fontWeight: active ? 600 : 400,
                    border: active ? `2px solid ${theme.color.accent}` : "1px solid var(--c-border)",
                    background: active ? "var(--c-accentBg)" : "var(--c-panel)",
                    color: active ? "var(--c-accentText)" : theme.color.muted,
                    cursor: "pointer",
                    textTransform: "capitalize",
                  }}
                >
                  {f === "all" ? "All" : f === "noncompliant" ? "Non-compliant" : f.charAt(0).toUpperCase() + f.slice(1)}
                </button>
              );
            })}
          </div>

          {/* Device table */}
          {devices.length === 0 ? (
            <div style={{ ...emptyFrame, padding: "32px 20px", color: theme.color.muted, fontSize: 13 }}>
              No devices match this filter.
            </div>
          ) : (
            <div style={{ ...card, padding: 0, overflow: "hidden" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                <thead>
                  <tr style={{ borderBottom: "1px solid var(--c-line)", background: theme.color.panelAlt }}>
                    {["Device", "User", "OS", "Version", "Status", "Last Device Sync"].map((h) => (
                      <th key={h} style={{ padding: "10px 14px", textAlign: "left", fontWeight: 600, fontSize: 12, color: theme.color.muted, whiteSpace: "nowrap" }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {devices.map((d, i) => (
                    <tr
                      key={d.id}
                      style={{ borderBottom: i < devices.length - 1 ? `1px solid ${theme.color.border}` : "none" }}
                    >
                      <td style={{ padding: "10px 14px", maxWidth: 220 }}>
                        <div style={{ fontWeight: 500, color: theme.color.heading, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{d.deviceName}</div>
                        {d.manufacturer && d.model && (
                          <div style={{ fontSize: 11, color: theme.color.subtle, fontWeight: 400, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{d.manufacturer} {d.model}</div>
                        )}
                      </td>
                      <td style={{ padding: "10px 14px", color: theme.color.muted, maxWidth: 160, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{d.userDisplayName ?? "—"}</td>
                      <td style={{ padding: "10px 14px", color: theme.color.muted, maxWidth: 120, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{d.operatingSystem ?? "—"}</td>
                      <td style={{ padding: "10px 14px", color: theme.color.muted, fontFamily: "monospace", fontSize: 12, maxWidth: 130, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{d.osVersion ?? "—"}</td>
                      <td style={{ padding: "10px 14px" }}><StatePill state={d.complianceState} /></td>
                      <td style={{ padding: "10px 14px", color: theme.color.muted, whiteSpace: "nowrap" }}>{fmtDate(d.lastSyncDateTime)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}
